import { useCallback, useEffect, useState } from "react";
import { CustomModal, modalZIndex } from "../../../components/CustomModal"
import { useTranslation } from "react-i18next";
import API from "../../../service/api";
import { MDBBtn, MDBIcon, MDBInput } from "mdb-react-ui-kit";
import { CustomSelect } from "../../../components/CustomSelect";
import { Alert } from "../../../components/Alert";
import { TextInput } from "../../../components/core/inputs/TextInput";

export interface AddSubcompanyModalProps {
    open: boolean;
    countries: any[];
    onClose: () => void;
    onAdd: () => void;
}
export const AddSubcompanyModal = ({open, countries, onClose, onAdd}: AddSubcompanyModalProps) => {
    const [company, setCompany] = useState({
        name: '',
        CIF: '',
        maxRightsId: -1,
        country: 'gb'
    });
    const [permissions, setPermissions] = useState<any[]>([]);
    const [changed, setChanged] = useState(false);
    const [addError, setAddError] = useState('');
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();

    const onChange = useCallback((key: string, val: any) => {
        setCompany(prev => ({
            ...prev,
            [key]: val
        }));
        setChanged(true);
        setAddError('');
    }, []);

    useEffect(() => {
        setChanged(false);
        setAddError('');
        (async () => {
            try {
                setPermissions((await API.GET('/api/rights')).rights);
            }catch(err: any){
                setAddError(err.message);
            }
        })();
    }, []);

    const submit = useCallback(async () => {
        setLoading(true);
        try {
            await API.POST('/api/companies/subcompanies', {
                name: company.name,
                CIF: company.CIF,
                maxRightsId: company.maxRightsId,
                country: company.country
            });
            onAdd();
            onClose();
        }catch(err: any){
            setAddError(err.message);
        }finally{
            setLoading(false);
        }
    }, [company]);

    useEffect(() => {
        if (!open) return;
        setCompany({
            name: '',
            CIF: '',
            maxRightsId: -1,
            country: 'gb'
        });
    }, [open]);

    return (
        <CustomModal
            open={open}
            title={t('add_subcompany')}
            onClose={onClose}
            footer={(
                <MDBBtn disabled={!changed || loading} onClick={submit}>
                    {t('add')}
                </MDBBtn>
            )}
        >
            <TextInput label={t('name')} icon="pen-to-square" value={company.name} onValueChange={val => onChange('name', val)}/>
            <TextInput label={t('cif')} icon="briefcase" value={company.CIF} onValueChange={val => onChange('CIF', val)}/>
            <div className="d-flex mb-2 flex-row align-items-center w-100">
                <MDBIcon icon="fas fa-briefcase" fixed className="me-2"/>
                <CustomSelect w100 title={t('permissions')} items={permissions.map(p => ({ val: p.id, title: p.Name }))} selected={company.maxRightsId} onSelect={id => {setCompany((c: any) => ({...c, maxRightsId: id})); setChanged(true); }} zIndex={modalZIndex+1}/>
            </div>
            <div className="d-flex mb-2 flex-row align-items-center w-100">
                <MDBIcon icon="fas fa-earth-europe" fixed className="me-2"/>
                <CustomSelect w100 title={t('country')} search={true} items={countries.map(c => ({ val: c.code, title: `${c.country}`, icon: `flag-icon flag-icon-${c.code?.toLocaleLowerCase()}`}))} selected={company.country} onSelect={code => {setCompany((comp: any) => ({...comp, country: code})); setChanged(true); }} zIndex={modalZIndex} />
            </div>
            <Alert type="danger" message={addError}/>
        </CustomModal>
    )
}